import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { logError } from './logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISMISS_URL = {
  contact: (id) => `${API}/business/contacts/${id}/reminders/dismiss`,
  client: (id) => `${API}/business/clients/${id}/reminders/dismiss`,
  team: (id) => `${API}/business/team/${id}/reminders/dismiss`,
};

/**
 * Promemoria Business scaduti (oggi o prima) di Contatti, Clienti e Il Mio Team, privati
 * per admin. Una persona con piu' promemoria compare come piu' voci distinte in `items`,
 * ognuna taggata con `type` ("contact"/"client"/"team") per poter distinguere le categorie in UI.
 */
export function useDueBusinessReminders(enabled = true) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!enabled) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/business/clients/due-reminders`);
      setItems(res.data?.items || []);
    } catch (err) {
      logError('useDueBusinessReminders', err);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { load(); }, [load]);

  const dismiss = useCallback(async (item) => {
    const { id, type, date } = item;
    try {
      await axios.post(DISMISS_URL[type](id), { date });
      setItems((prev) => prev.filter((it) => !(it.id === id && it.type === type && it.date === date)));
    } catch (err) {
      logError('useDueBusinessReminders', err);
    }
  }, []);

  const contactsCount = useMemo(() => items.filter((it) => it.type === 'contact').length, [items]);
  const clientsCount = useMemo(() => items.filter((it) => it.type === 'client').length, [items]);
  const teamCount = useMemo(() => items.filter((it) => it.type === 'team').length, [items]);

  return { items, count: items.length, contactsCount, clientsCount, teamCount, loading, reload: load, dismiss };
}
